import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * The shared vocabulary. Every status dot, KPI figure and gauge on the console comes
 * from here, because the moment two screens invent their own "warning amber" the colour
 * stops meaning anything and starts being decoration.
 */

export type Status = 'moving' | 'idle' | 'charging' | 'stale' | 'fault'

const STATUS: Record<Status, { dot: string; text: string; bg: string; label: string }> = {
  moving:   { dot: '#00C896', text: 'text-ok',       bg: 'bg-ok-dim',       label: 'Moving' },
  charging: { dot: '#3B82F6', text: 'text-charging', bg: 'bg-charging-dim', label: 'Charging' },
  idle:     { dot: '#F5A623', text: 'text-warn',     bg: 'bg-warn-dim',     label: 'Idle' },
  stale:    { dot: '#4F566B', text: 'text-muted',    bg: 'bg-white/[0.04]', label: 'No signal' },
  fault:    { dot: '#F04B4B', text: 'text-danger',   bg: 'bg-danger-dim',   label: 'Fault' },
}

export function StatusDot({ status, pulse }: { status: Status; pulse?: boolean }): JSX.Element {
  return (
    <span
      className={`inline-block h-[7px] w-[7px] shrink-0 rounded-full ${pulse ? 'animate-pulse-dot' : ''}`}
      style={{ background: STATUS[status].dot }}
    />
  )
}

export function StatusBadge({ status, children }: { status: Status; children?: ReactNode }): JSX.Element {
  const s = STATUS[status]
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-2xs ${s.bg} ${s.text}`}>
      <StatusDot status={status} pulse={status === 'fault'} />
      {children ?? s.label}
    </span>
  )
}

/**
 * A KPI figure. The value is the loudest thing in the cell and the label is the
 * quietest, because an operator glancing at the strip is reading numbers, not nouns.
 * The accent bar carries status without spending a second colour on the digits.
 */
export function Kpi({
  label, value, unit, accent = 'none', sub, onClick,
}: {
  label: string
  value: ReactNode
  unit?: string
  accent?: 'none' | 'ok' | 'warn' | 'danger' | 'info'
  sub?: ReactNode
  onClick?: () => void
}): JSX.Element {
  const bar = {
    none: 'bg-line-strong', ok: 'bg-ok', warn: 'bg-warn',
    danger: 'bg-danger', info: 'bg-info',
  }[accent]
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={`relative flex min-w-[104px] flex-col gap-0.5 overflow-hidden rounded-md
                  border border-line bg-raised px-3 py-2 text-left transition-colors
                  ${onClick ? 'hover:border-line-strong' : ''}`}
    >
      <span className={`absolute inset-y-0 left-0 w-[3px] ${bar}`} />
      <span className="label">{label}</span>
      <span className="flex items-baseline gap-1">
        <span className="num font-mono text-xl font-medium leading-none text-ink">{value}</span>
        {unit && <span className="text-2xs text-faint">{unit}</span>}
      </span>
      {sub && <span className="text-2xs text-muted">{sub}</span>}
    </Tag>
  )
}

/**
 * State of charge as an arc.
 *
 * A bar would do, but SoC is the number an EV operator looks at before any other and an
 * arc reads at a glance from across a room. Colour is bound to the operational
 * thresholds, not to a gradient: below 15% is a bus that may not finish its leg.
 */
export function SocArc({ value, size = 92 }: { value: number | null; size?: number }): JSX.Element {
  const r = size / 2 - 8
  const cx = size / 2
  const cy = size / 2
  // 270° sweep, opening at the bottom.
  const start = 135
  const sweep = 270
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value)) / 100
  const arc = (from: number, to: number): string => {
    const p = (a: number): [number, number] => [
      cx + r * Math.cos((a * Math.PI) / 180),
      cy + r * Math.sin((a * Math.PI) / 180),
    ]
    const [x1, y1] = p(from)
    const [x2, y2] = p(to)
    return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`
  }
  const color = value == null ? '#4F566B'
    : value < 15 ? '#F04B4B' : value < 35 ? '#F5A623' : '#00C896'

  return (
    <svg width={size} height={size} className="shrink-0">
      <path d={arc(start, start + sweep)} fill="none" stroke="rgba(255,255,255,0.07)"
            strokeWidth={7} strokeLinecap="round" />
      {value != null && pct > 0 && (
        <path d={arc(start, start + sweep * pct)} fill="none" stroke={color}
              strokeWidth={7} strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 400ms ease-out' }} />
      )}
      <text x={cx} y={cy + 2} textAnchor="middle"
            className="num fill-ink font-mono" fontSize={size / 4.2} fontWeight={500}>
        {value == null ? '—' : value}
      </text>
      {value != null && (
        <text x={cx} y={cy + size / 5.2} textAnchor="middle"
              className="fill-faint" fontSize={size / 9}>%</text>
      )}
    </svg>
  )
}

/** Empty state. Says what is missing, why, and what makes it appear. */
export function Empty({
  icon, title, children,
}: { icon?: ReactNode; title: string; children?: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      {icon && <span className="text-faint">{icon}</span>}
      <p className="font-display text-sm font-medium text-muted">{title}</p>
      {children && <p className="max-w-[46ch] text-xs leading-relaxed text-faint">{children}</p>}
    </div>
  )
}

/**
 * A count that rolls to its value instead of popping. Used for row/result counters --
 * always render it in a tabular-nums context so the rolling digits do not shift their
 * neighbours. Honours prefers-reduced-motion by snapping straight to the value (the
 * global CSS rule cannot reach a rAF loop, so the check lives here).
 */
export function CountUp({ value, duration = 450 }: { value: number; duration?: number }): JSX.Element {
  const [shown, setShown] = useState(0)
  const prevRef = useRef(0)
  useEffect(() => {
    const from = prevRef.current
    prevRef.current = value
    if (from === value) return
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setShown(value)
      return
    }
    const t0 = performance.now()
    let raf = 0
    const tick = (t: number): void => {
      const p = Math.min(1, (t - t0) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setShown(Math.round(from + (value - from) * eased))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, duration])
  return <>{shown.toLocaleString('en-IN')}</>
}

export function SkeletonRows({ rows = 6 }: { rows?: number }): JSX.Element {
  return (
    <div className="space-y-1.5 p-2">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton h-6" style={{ opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  )
}
